/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { EbtHookReturnType } from '@modules/ebt/hook'
import useEbt from '@modules/ebt/hook'

const DELETION_SURVEY_CLOSE_EVENT = 'deletion_survey_close' as const
const DELETION_SURVEY_SUBMIT_EVENT = 'deletion_survey_submit' as const

const EVENT_TYPES = [DELETION_SURVEY_CLOSE_EVENT, DELETION_SURVEY_SUBMIT_EVENT] as const
type EventType = (typeof EVENT_TYPES)[number]

type DeletionSurveyCloseEventPayloadSchema = {
  eventType: typeof DELETION_SURVEY_CLOSE_EVENT
  payload: {
    type: string
    solution?: string
    region?: string
  }
}

type DeletionSurveySubmitEventPayloadSchema = {
  eventType: typeof DELETION_SURVEY_SUBMIT_EVENT
  payload: {
    type: string
    solution?: string
    region?: string
    score: number
    reasonForScore: string
    deletionReason: string
    openToFollowUp: boolean
  }
}

type EventPayloadSchema =
  | DeletionSurveyCloseEventPayloadSchema
  | DeletionSurveySubmitEventPayloadSchema

const useEbtDeletionSurvey = (): EbtHookReturnType<EventType, EventPayloadSchema> => {
  const reportEbtEvent = useEbt<EventPayloadSchema>(
    {
      eventType: DELETION_SURVEY_CLOSE_EVENT,
      schemaProps: {
        type: {
          keywordType: 'keyword',
          description: 'The type of resource',
        },
        solution: {
          keywordType: 'keyword',
          description: 'The solution type',
          optional: true, // optional because it is not available for all types of resources
        },
        region: {
          keywordType: 'keyword',
          description: 'The region identifier',
          optional: true,
        },
      },
    },
    {
      eventType: DELETION_SURVEY_SUBMIT_EVENT,
      schemaProps: {
        type: {
          keywordType: 'keyword',
          description: 'The type of resource',
        },
        solution: {
          keywordType: 'keyword',
          description: 'The solution type',
          optional: true, // optional because it is not available for all types of resources
        },
        region: {
          keywordType: 'keyword',
          description: 'The region identifier',
          optional: true,
        },
        score: {
          keywordType: 'integer',
          description: 'Score for how likely user is to recommend a resource to a friend',
        },
        reasonForScore: {
          keywordType: 'text',
          description: 'The reason provided for the score given',
        },
        deletionReason: {
          keywordType: 'keyword',
          description: 'The primary reason for deleting the resource',
        },
        openToFollowUp: {
          keywordType: 'boolean',
          description: 'Whether the user is open to follow-up outreach from the product team',
        },
      },
    },
  )

  return {
    reportDeletionSurveyCloseEbt: ({ type, solution, region }) => {
      reportEbtEvent(DELETION_SURVEY_CLOSE_EVENT, {
        type,
        solution,
        region,
      })
    },
    reportDeletionSurveySubmitEbt: ({
      type,
      solution,
      region,
      score,
      reasonForScore,
      deletionReason,
      openToFollowUp,
    }) => {
      reportEbtEvent(DELETION_SURVEY_SUBMIT_EVENT, {
        type,
        solution,
        region,
        score,
        reasonForScore,
        deletionReason,
        openToFollowUp,
      })
    },
  }
}

export default useEbtDeletionSurvey
